import React, { useState, useMemo, useEffect } from 'react';

import * as Yup from 'yup';
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Backdrop,
  FormControlLabel,
  Checkbox,
  TextField,
  Paper,
} from '@mui/material';
import { Button, Form } from 'src/components/shared';
import { tenantPartnersLookup } from 'src/modules/reseller/api/resellerApis';
import {
  isTenantApprovedPartner,
  isTenantApprovedReseller,
} from 'src/modules/app/actions/appSelector';
import { useSelector } from 'react-redux';
import SeqTypeTab from './SeqTypeTab';
import { useStyles } from './styles';
import { fetchBrandVoice } from '../../../api/sequenceApi';
import { useSocketStore } from '../../../../../components/hooks/socketConnector';
import toast from 'src/utils/toast';
import AiSequenceGenerator from './AiSequenceGenerator';
import { deepParse } from 'src/utils/helper';

const stepsList = [
  {
    label: 'LinkedIn connections',
    value: 'linkedinConnection',
  },
  {
    label: 'LinkedIn message',
    value: 'linkedinMessage',
  },
  {
    label: 'LinkedIn InMail',
    value: 'linkedinMail',
  },
  {
    label: 'LinkedIn view profile',
    value: 'linkedinViewProfile',
  },
  {
    label: 'Email',
    value: 'email',
  },
  {
    label: 'Call back',
    value: 'call',
  },
  {
    label: 'SMS',
    value: 'sms',
  },
  {
    label: 'Tasks',
    value: 'general',
  },
];

function AIWriteForm({
  sequenceAccessTypeList,
  sequenceEmailTypeList,
  user,
  isAIWriter,
  sequence,
  ...props
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [listOfVoice, setListOfVoice] = useState([]);
  const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);

  const isReseller = useSelector((state) => isTenantApprovedReseller(state));
  const isPartner = useSelector((state) => isTenantApprovedPartner(state));
  const sequenceCategories = useSelector((state) => state.app.globals?.sequenceCategories);
  const { emit, aiResponse, socket } = useSocketStore();
  const aiFormFieldsWithValue = useSelector((state) => state.app.globals?.aiFormFieldsWithValue);

  const getPurposeOptions = (values) => {
    const typeValueMap = {
      work: 'Client',
      personal: 'Candidate',
      other: 'Other',
    };
    if (aiFormFieldsWithValue?.Purpose) {
      const label = typeValueMap[values?.audienceEmailType]; // Find the label from the value
      return aiFormFieldsWithValue?.Purpose?.find(
        (item) => item?.optionValue === label,
      )?.fields?.map((item) => ({ label: item, value: item }));
    }
    return [];
  };

  const fetchOption = async (val) => {
    setLoading(true);
    try {
      const query = val && val;
      const response = await fetchBrandVoice(query);
      setOptions(response);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching options:', err);
      setOptions([]);
      setLoading(false);
    }
  };

  const fetchToneOfVoice = async (val) => {
    setIsVoiceLoading(true);
    try {
      const query = val ? val : '';
      const response = await fetchBrandVoice(query);
      setIsVoiceLoading(false);
      return response;
    } catch (err) {
      setIsVoiceLoading(false);
      return [];
    }
  };

  useEffect(() => {
    if (isAIWriter) {
      (async () => {
        const res = await fetchBrandVoice();
        setListOfVoice(res);
      })();
    }
    (async () => {
      await fetchOption('');
    })();
  }, []);

  const defaultVoice = options?.find((v) => v.isDefault);

  const handleSubmit = async (values, form) => {
    setLoading(true);
    const data = JSON.parse(JSON.stringify(values));
    if (sequence.id) {
      data.id = sequence.id;
    }
    if (data?.partner) {
      if (Object.keys(data?.partner).length === 0 || data?.category !== 'reseller')
        delete data.partner; // comes as empty {}
    }

    data.partner = data?.partner && data?.partner?.id ? data?.partner.id : null;
    data.sharedWithTeams = data.sharedWithTeams.map((team) => team.id);
    data.sharedWithUsers = [user?.id];
    data.testBatch = 200;
    data.variancePercentage = 15;
    data.winnerPercentage = 75;
    data.allowRestart = false;
    data.trackClick = false;
    data.purpose = data.category;
    data.furtherInstruction = data.additionalInstructions ?? '';

    if (isAIWriter) {
      data.name = '';
      data.description = '';

      if (data?.toneOfVoice?.voicePrompt) {
        data.toneOfVoice = deepParse(data?.toneOfVoice?.voicePrompt);
        delete data?.toneOfVoice?.tovGuidelines?.Tone_of_Voice;
      }

      emit(
        'pipeline:execute',
        JSON.stringify({
          slug: 'create-sequence',
          payload: { text: JSON.stringify({ ...data }) },
        }),
      );

      const handleSubmitSequence = async (res) => {
        setLoading(true);
        try {
          const payload = { ...data, ...res?.json, toneOfVoice: values?.toneOfVoice?.id };
          const isShowBodyEvent = ['email', 'wait'];

          if (payload?.events?.length) {
            payload.events = payload?.events?.map((event) =>
              !isShowBodyEvent.includes(event.eventType)
                ? {
                    ...event,
                    templateProps: event.content ?? {},
                    description:
                      typeof event?.content === 'string'
                        ? event?.content
                        : event?.content?.body ?? event.description ?? '',
                  }
                : event,
            );
          }

          if (payload?.id) {
            if (sequence.events && sequence?.events?.length) {
              payload.prevEvents = [...sequence.events];
            }

            await props.onSubmit({ ...payload, isAIWriter }, payload.id, () => {
              setLoading(false);
            });
          } else {
            await props.onSubmit({ ...payload, isAIWriter }, null, () => {
              setLoading(false);
            });
          }
          socket.off('pipeline:execute:success', handleSubmitSequence);
        } catch (e) {
          console.error(e);
        }
        setLoading(false);
      };

      socket.on('pipeline:execute:success', handleSubmitSequence);

      const handleDisconnect = () => {
        setLoading(false);
        toast.warning('AI service temporarily unavailable');
        socket.off('disconnect', handleDisconnect);
      };

      socket.on('disconnect', handleDisconnect);

      socket.on('pipeline:execute:error', () => {
        setLoading(false);
      });
    } else {
      delete data.toneOfVoice;
      delete data.stepsToGenerate;
      delete data.furtherInstruction;
      if (sequence) {
        await props.onSubmit(data, sequence.id, () => {
          setLoading(false);
        });
      } else {
        await props.onSubmit(data, null, () => {
          setLoading(false);
        });
      }
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e, formProps) => {
    const { value, checked } = e.target;
    const currentSteps = formProps?.values?.stepsToGenerate || [];

    if (checked) {
      formProps.setFieldValue('stepsToGenerate', [...currentSteps, value]);
    } else {
      formProps.setFieldValue(
        'stepsToGenerate',
        currentSteps.filter((step) => step !== value),
      );
    }
  };

  const aiType = useMemo(() => {
    const typeValueMap = {
      Client: 'work',
      Candidate: 'personal',
      Other: 'other',
    };

    if (aiFormFieldsWithValue?.Type) {
      setLoading(false); // Set loading to false once we have the data
      return aiFormFieldsWithValue?.Type.map((type) => {
        return {
          label: type,
          value: typeValueMap[type] || type, // Default to type if not in the map
        };
      });
    }
    setLoading(true); // Set loading to true if there's no data yet
    return [];
  }, [aiFormFieldsWithValue?.Type]);

  return (
    <>
      {props?.loading || loading ? (
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute' }}
          open={props?.loading || loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}
      <Box className={classes.card}>
        {!isAIWriter && (
          <Typography
            fontWeight={500}
            fontSize={'18px'}
            color={'#000000DE'}
            sx={{ margin: '16px 24px' }}
          >
            Create a new sequence
          </Typography>
        )}
        <Form
          initialValues={{
            name: sequence?.name || '',
            description: sequence?.description || '',
            accessType: sequence?.accessType || 'private',
            audience: sequence?.audience || 'Customer',
            audienceEmailType: sequence?.audienceEmailType || 'work',
            sharedWithUsers: [{ value: user?.id, name: user?.fname }],
            sharedWithTeams: teams,
            status: 'unpublished',
            category: sequence?.category,
            toneOfVoice: sequence?.toneOfVoice ? sequence?.toneOfVoice : defaultVoice,
            partner: {},
            stepsToGenerate: sequence?.stepsToGenerate || [],
            furtherInstruction: sequence?.furtherInstruction || '',
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().when('isAIWriter', {
              is: false,
              then: Yup.string().required('Please enter sequence name.'),
            }),
            // name: Yup.string().required('Please enter sequence name.'),
            // description: Yup.string().required('Please enter sequence description.'),
            audienceEmailType: Yup.string().required('Please select sequence type.'),
            accessType: Yup.string().required('Please select Sequence access.'),
            category: Yup.string().required('Please select the purpose of your email.'),
            partner: Yup.object().test('reseller', 'Please choose a Partner', function (item) {
              if (this.parent.category === 'reseller') {
                return item?.id && item?.id !== '';
              }
              return true;
            }),
            sharedWithUsers: Yup.array().test({
              message: () => 'Please choose at least one user.',
              test: (value, values) => {
                const accessType = values.parent.accessType;
                if (accessType === 'private') {
                  return value.length > 0;
                }
                return true;
              },
            }),
            sharedWithTeams: Yup.array().test({
              message: () => 'Please choose at least one team.',
              test: (value, values) => {
                const accessType = values.parent.accessType;
                if (accessType === 'team') {
                  return value.length > 0;
                }
                return true;
              },
            }),
          })}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ values, ...formProps }) => {
            return (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formProps.submitForm();
                  return false;
                }}
                id="create-sequence-form"
                style={{ minHeight: '365px', height: '100%', width: '100%' }}
                noValidate
              >
                {isAIWriter ? (
                  <AiSequenceGenerator
                    getPurposeOptions={getPurposeOptions}
                    values={values}
                    formProps={formProps}
                    isVoiceLoading={isVoiceLoading}
                    fetchToneOfVoice={fetchToneOfVoice}
                    onClose={props?.onClose}
                  />
                ) : (
                  <Box
                    display="flex"
                    flex={1}
                    flexDirection="column"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    height="100%"
                  >
                    <Box width="100%">
                      <Grid container sx={{ padding: '0px 24px' }}>
                        <>
                          <Grid item xs={2} container alignItems="center">
                            <Typography className={classes.label}>Name</Typography>
                          </Grid>
                          <Grid item xs={10}>
                            <Form.Field.Input
                              fullWidth
                              variant="outlined"
                              name="name"
                              hideAsterisk
                              label="Sequence name"
                              style={{ marginBottom: '0px' }}
                              size="small"
                            />
                          </Grid>
                        </>

                        {aiType?.length > 0 && (
                          <>
                            <Grid item xs={2} container alignItems="center">
                              <Typography className={classes.label}>Sequence type</Typography>
                            </Grid>
                            <Grid item xs={10} sx={{ marginTop: '16px' }}>
                              <SeqTypeTab
                                value={values?.audienceEmailType}
                                onClick={async (value) => {
                                  await formProps?.setFieldValue('audienceEmailType', value);
                                  await formProps?.setFieldValue('category', '');
                                }}
                                formProps={formProps}
                                seqTypeList={aiType}
                              />
                            </Grid>
                          </>
                        )}

                        <Grid item xs={2} container alignItems="center">
                          <Typography className={classes.label}>Purpose</Typography>
                        </Grid>
                        <Grid item xs={10}>
                          <Form.Field.Select
                            options={getPurposeOptions(values)}
                            placeholder={'Please select'}
                            fullWidth
                            hideAsterisk
                            size="small"
                            variant="outlined"
                            name="category"
                            optLabel="label"
                            optValue="value"
                          />
                        </Grid>

                        {values?.category === 'reseller' && isReseller && (
                          <>
                            <Grid item xs={2} />
                            <Grid item xs={10}>
                              <Form.Field.AutoComplete
                                multiple={false}
                                options={[]}
                                fullWidth
                                size="small"
                                variant="outlined"
                                name="partner"
                                label="Partner"
                                remoteMethod={(val) => {
                                  return tenantPartnersLookup('', val);
                                }}
                                optLabel="name"
                                optValue="id"
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Box>
                )}
              </form>
            );
          }}
        </Form>
      </Box>
    </>
  );
}

export default AIWriteForm;
